import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import * as Yup from 'yup';
import Input from '../../reusable/Input/Input';
import NumberInput from '../../reusable/NumberInput/NumberInput';
import Select from '../../reusable/Select/Select';
import {
  useCreateFreeTrialMutation,
  useCreatePaymentUrlMutation,
  useCheckCountryQuery,
  useGetChallengesQuery,
  useGetFinalPriceQuery,
  usePreviewAccountMutation,
} from '../../store/api/checkoutApi';
import {
  useGetProfileDetailsQuery,
  useUpdateProfileDetailsMutation,
} from '../../store/api/profileApi';
import { toast } from '../../reusable/Toast/toastStore';
import {
  countryName,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_DIAL_CODE,
  findCountry,
} from '../../utilities/country';
import { usd } from '../../utilities/format';
import { formatAccountSize } from '../../utilities/accounts';
import { selectRefreshToken, selectUser } from '../../store/slices/authSlice';
import { selectAccount } from '../../store/slices/accountSlice';
import { t } from '../../i18n/i18n';
import CheckoutSkeleton from './CheckoutSkeleton';
import CryptoWarningDialog from './CryptoWarningDialog';
import PurchaseReviewDialog from './PurchaseReviewDialog';
import TermsViewer from './TermsViewer';
import useSelectionFill from './useSelectionFill';
import {
  addOns,
  checkoutSteps,
  CONSENT_REQUIRED,
  consents,
  eaChoices,
  NO_ADDON_PLANS,
  NO_EA_PLANS,
  NO_SWAP_FREE_PLANS,
  paymentMethods,
  payoutTypes,
  platforms,
  spreads,
  stepIcons,
  TERMS_PDF,
} from './data';
import './TradingAssessment.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * The three selection rows fill the card's width with one equal column per
 * option, up to `cap` per row — six size pills as the frame draws them, four
 * anywhere else. The catalogue is only known at render time, so the count goes
 * to the grid as a custom property; narrower screens take over from there with
 * their own `auto-fit` track counts.
 */
const cols = (count, cap = 4) => ({ '--ta-cols': Math.min(count, cap) });

/**
 * Fee cards show cents ($50.00), the total drops them ($50) — 1:12352 / 1:12542.
 * A discount is what puts cents in the total, and the frame never draws one, so
 * the rule holds only while the figure is whole: $47, but $37.60 rather than the
 * "$37.6" that "up to two decimals" would otherwise print.
 */
const totalDigits = (value) => (Number.isInteger(value) ? undefined : { decimals: 2 });

/**
 * `GET /v3/challenges/` returns Django model rows: a step → plan type →
 * challenge tree with the numbers as strings. Reshape it into what the three
 * selection groups render.
 *
 * Every row the endpoint returns is offered, `publish` included. That flag is
 * the backend's not-for-sale switch and was filtered on until AC1-I87: it is
 * false on all six Alpha Pro 10% sizes, both of the unlisted Alpha One plans
 * (6% and 12%) and Alpha Direct 1K, so honouring it dropped three whole plan
 * types from the picker. `/v2/get/final-price/` prices those rows regardless,
 * so they are sellable as far as the checkout is concerned.
 *
 * The empty-plan and empty-step guards below stay: nothing trims the lists now,
 * but the endpoint can still answer with a plan that carries no challenges.
 */
const buildCatalogue = (response = []) =>
  response
    .map((step) => ({
      // The API names steps in prose ("Instant Qualified", "Step 1"), with no id.
      id: step.step,
      label: step.step,
      plans: (step.plans ?? [])
        .map((plan) => ({
          id: plan.plan_type,
          label: plan.plan_type,
          challenges: (plan.challenges ?? [])
            .map((c) => ({
              id: c.id,
              name: c.name,
              balance: Number(c.account_balance),
              // One price per payout type: `Price` is the bi-weekly figure and
              // `ondemand_price` the on-demand one. They diverge on 19 of the 52
              // rows — every Alpha Pro size and Alpha Direct 1K. Alpha Pro 10K
              // 6% is $55 bi-weekly against $47 on demand, and quoting the wrong
              // one is what `/final-price/` then contradicts. Only Free Trial
              // leaves `ondemand_price` unset, and it is free under either.
              price: {
                biweekly: Number(c.Price),
                ondemand: Number(c.ondemand_price ?? c.Price),
              },
              // Same plan at a 90% profit split — what the step-2 add-on costs.
              // On-demand only: there is no bi-weekly 90% figure in the row, and
              // the endpoint prices `profit_split=90` as 80 on that branch.
              // Upstream spells it "ninty".
              ninetySplitPrice: Number(c.ninty_pfs_price),
              // The payout type the challenge is natively sold on. 1 (bi-weekly)
              // is where the Payout Type choice exists at all; on 2 both prices
              // above are identical, so there is nothing to choose between.
              payoutType: c.payout_type,
              // 0 on Instant Qualified, 1 or 2 on the evaluation steps.
              stepCount: c.steps,
              // The row as the endpoint sent it — Show Objectives reads the
              // plan rules (`Leverage`, `Max_Loss`, `trailing_mll`…) off it.
              row: c,
            }))
            .sort((a, b) => a.balance - b.balance),
        }))
        .filter((plan) => plan.challenges.length > 0),
    }))
    .filter((step) => step.plans.length > 0);

/** 2000000 → "$2M": the capital-growth ceiling is only ever quoted in millions. */
const compactUsd = (value) => {
  const n = Number(value);
  return n >= 1e6 ? `$${n / 1e6}M` : usd(n);
};

/** A rule's dollar figure as a share of the account: 600 on 10,000 → "6%". */
const pctOf = (value, balance) => {
  const n = Number(value);
  if (!n || !balance) return '—';
  const pct = (n / balance) * 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
};

/**
 * "Show Objectives" — the plan rules the old checkout lists before purchase
 * (its `renderPlanDetails`), read off the selected challenge's own row:
 *
 * - profit targets are `Evaluation_Phase_N` in dollars; a phase of 0 or null
 *   isn't part of the plan, and Free Trial never lists a second target
 * - Alpha One draws down on a trailing limit (`trailing_mll`, already a percent)
 *   instead of the fixed `Max_Loss`
 * - Max Capital Growth is only stated on the two-step evaluations
 */
const planObjectives = ({ row, balance, planType, stepCount, profitSplit, payoutLabel }) => {
  const isFreeTrial = planType === 'free trial';
  const isAlphaDirect = planType === 'alpha direct';
  // Trailing wherever the plan's row says so — Alpha One and Alpha Direct send
  // `trailing_mll` above 0, the static plans send 0.
  const trailing = Number(row.trailing_mll) > 0;
  const targets = [row.Evaluation_Phase_1, row.Evaluation_Phase_2, row.Evaluation_Phase_3]
    .filter((t) => Number(t) > 0)
    .slice(0, isFreeTrial ? 1 : 3);

  return [
    [t('Leverage'), row.Leverage ? t('Up to {{value}}', { value: `1:${row.Leverage}` }) : '—'],
    ...targets.map((target, i) => [
      targets.length > 1 ? t('Profit Target {{n}}', { n: i + 1 }) : t('Profit Target'),
      pctOf(target, balance),
    ]),
    trailing
      ? [t('Trailing Max Loss'), `${row.trailing_mll}%`]
      : [t('Max Loss'), pctOf(row.Max_Loss, balance)],
    [t('Max Daily Loss'), pctOf(row.Max_Daily_Loss, balance)],
    [t('Profit Split'), `${profitSplit}%`],
    ...(isFreeTrial
      ? []
      : [[t('Payout'), isAlphaDirect ? t('15% consistency + 1% minimum profit') : t(payoutLabel)]]),
    ...(stepCount === 2 && !isFreeTrial && Number(row.Max_Capital_Growth) > 0
      ? [[t('Max Capital Growth'), t('Up to {{value}}', { value: compactUsd(row.Max_Capital_Growth) })]]
      : []),
  ];
};

/** "Instant funding" for the 0-step plan, "1-phase evaluation" for the rest. */
const stepNote = (stepCount) =>
  stepCount > 0 ? t('{{n}}-phase evaluation', { n: stepCount }) : t('Instant funding');

/**
 * Customer Information rules, ported from the old checkout's `checkValidate`
 * (PaymentExports.js): names letters-only up to 70, email 50, phone digits-only
 * 6–15, city and country required.
 *
 * One deliberate difference — the old function tests `details.city` for digits
 * while every other branch reads `details.City`, so that check never fires
 * there. It fires here.
 */
const nameRule = (label) =>
  Yup.string()
    .trim()
    .required(`${label} is required`)
    .max(70, 'Must be at most 70 characters')
    .matches(/^[^0-9]*$/, 'Only letters, spaces, and hyphens allowed');

const customerSchema = Yup.object({
  first_name: nameRule('First Name'),
  last_name: nameRule('Last Name'),
  email: Yup.string()
    .trim()
    .required('Email is required')
    .max(50, 'Email must be at most 50 characters')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format'),
  contact_number: Yup.string()
    .required('Phone number is required')
    .matches(/^\d+$/, 'Phone number must contain only digits')
    .min(6, 'Phone number must be at least 6 digits')
    .max(15, 'Phone number must not exceed 15 digits'),
  city: nameRule('City'),
  country: Yup.string().required('Country is required'),
});

/**
 * What a failed checkout call is allowed to say. The two purchase endpoints
 * report problems in the body — a coupon that doesn't exist, a challenge that
 * stopped being for sale — so the body's own wording is preferred over a
 * status code the trader can do nothing with.
 */
const CHECKOUT_FAILED = 'We could not complete your purchase. Please try again.';

/**
 * Whatever the endpoint said went wrong, in its own words. DRF answers a bare
 * string as often as an object, and names the field `message`, `detail` or
 * `error` depending on the view, so each shape is read rather than assumed.
 */
const apiMessage = (err, fallback) => {
  const data = err?.data ?? err?.error?.data;
  if (typeof data === 'string' && data.trim()) return data;
  const message = data?.message ?? data?.detail ?? data?.error;
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
};

const checkoutError = (err) => apiMessage(err, CHECKOUT_FAILED);

/**
 * Only when `/v2/get/final-price/` rejects a code without saying why. It is
 * specific about the ones it does explain — "Invalid coupon" for a code it
 * doesn't know, and which plans a real code is restricted to — so those reach
 * the trader verbatim rather than being flattened into this.
 */
const COUPON_INVALID = 'Invalid coupon code.';

/**
 * The processor's hosted checkout URL. `/v2/get/checkout/payment-url/` has been
 * seen answering under more than one key depending on the processor, and DRF
 * will hand back a bare string for a plain `Response("https://…")`, so every
 * shape it can take is read rather than assuming one.
 */
/** A challenge sold bi-weekly by default is the one that offers the choice. */
const PAYOUT_TYPE_BI_WEEKLY = 1;

/** What every plan falls back to, and what the ones without a choice are sold on. */
const ON_DEMAND_PAYOUT = payoutTypes.find((p) => p.id === 'ondemand');

/**
 * The order as both purchase endpoints describe it. `null` means "not chosen"
 * and is dropped from the multipart body below — reproducing the old checkout,
 * which posts a field only once it has a value.
 */
const orderBody = ({
  refreshToken,
  paymentMethod,
  challengeId,
  coupon,
  customer,
  contact,
  platform,
  rawSpread,
  swapFree,
  profitSplit,
  payoutType,
  eaUse,
}) => ({
  refresh_token: refreshToken,
  payment_method: paymentMethod,
  challenge_id: challengeId,
  coupon: coupon || null,
  email: customer.email,
  confirmEmail: customer.email,
  first_name: customer.first_name,
  last_name: customer.last_name,
  contact,
  payout_type: payoutType,
  cryptocurrency: null,
  // The purchase endpoints' `country_code` is the ISO code ("GB"), not the
  // profile's dial code of the same name.
  country_code: findCountry(customer.country)?.code ?? customer.country,
  raw_spread: rawSpread || null,
  platform,
  // `null` on a platform that never asks, so the field stays off the body the
  // way it is today; a MetaTrader order always carries the trader's answer.
  EAs: eaUse,
  swap_free: swapFree,
  profit_split: profitSplit,
});

/** Multipart encoding, skipping the nulls; booleans go as "true"/"false". */
const toFormData = (body) => {
  const form = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    form.append(key, typeof value === 'boolean' ? String(value) : value);
  });
  return form;
};

/**
 * The number out of `/v2/get/final-price/`, which answers
 * `{ actual_price, discounted_price, discount, coupon_applied }`. The figure to
 * charge is `discounted_price` — it equals `actual_price` when no coupon
 * applies, and is the *only* key carrying the coupon's effect, so reading
 * anything else is how the summary ends up quoting the undiscounted price.
 * A bare figure is still accepted, and anything unreadable leaves the summary
 * on its local estimate.
 */
const finalPriceFrom = (data) => {
  const value =
    data && typeof data === 'object' ? (data.discounted_price ?? data.actual_price) : data;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const paymentUrlFrom = (data) => {
  if (typeof data === 'string') return data.trim() || null;
  if (!data || typeof data !== 'object') return null;
  const url =
    data.payment_url ?? data.url ?? data.checkout_url ?? data.redirect_url ?? data.link;
  return typeof url === 'string' && url.trim() ? url : null;
};

function Radio({ on }) {
  return (
    <span className={`ta-radio${on ? ' ta-radio--on' : ''}`}>
      {on && <span className="ta-radio__tick" style={icon('check-sm')} />}
    </span>
  );
}

/** The "Show Objectives" switch — beside Choose your Step, and in the summary on Checkout. */
function ObjectivesSwitch({ on, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      className="ta-objectives-switch"
      onClick={onToggle}
    >
      <span className="ta-objectives-switch__label">{t('Show Objectives')}</span>
      <span className={`ta-switch${on ? ' ta-switch--on' : ''}`} aria-hidden="true" />
    </button>
  );
}

/**
 * One plan's rules as label / value rows — `planObjectives` output. Spans, not
 * a `<dl>`: on Select Plan the list sits inside the Account Type tile's
 * `<button>`, which only takes phrasing content.
 *
 * Always mounted, so the switch can animate it: closed, the wrapper's single
 * grid track sits at `0fr` and clips the list; open, it eases to `1fr` — the
 * list's own height, with no measuring.
 */
function ObjectivesList({ rows, open }) {
  return (
    <span className={`ta-rules-wrap${open ? ' ta-rules-wrap--open' : ''}`} aria-hidden={!open}>
      <span className="ta-rules-wrap__clip">
        <span className="ta-rules">
          {rows.map(([label, value], i) => (
            <span key={label} className="ta-rules__row" style={{ '--i': i }}>
              <span className="ta-rules__label">{label}</span>
              <span className="ta-rules__value">{value}</span>
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}

/**
 * Customer Information, keyed and valued as `/v3/user/profile/details/` sends
 * it: `country_code` is the dial code as a number and `country` the country's
 * name. Only the two pickers convert, at their own props.
 */
const EMPTY_CUSTOMER = {
  first_name: '',
  last_name: '',
  email: '',
  contact_number: '',
  country_code: Number(DEFAULT_DIAL_CODE.replace(/\D/g, '')),
  city: '',
  country: countryName(DEFAULT_COUNTRY_CODE),
};

/** What the trader can write back to the profile — email is read-only there. */
const PROFILE_WRITABLE = ['first_name', 'last_name', 'contact_number', 'city', 'country'];

export default function TradingAssessment() {
  const { data, isLoading, isError, refetch } = useGetChallengesQuery();
  const catalogue = useMemo(() => buildCatalogue(data), [data]);
  // The visitor's country by IP, as the live checkout reads it. Until it
  // answers (or if it fails) the page behaves as for a non-US client.
  const { data: geo } = useCheckCountryQuery();
  const isUsClient = geo?.country === 'US';
  const user = useSelector(selectUser);
  // Both purchase endpoints authenticate on the session's Firebase refresh
  // token in the body, not on the `Authorization` header apiSlice attaches.
  const refreshToken = useSelector(selectRefreshToken);

  // 1 = Select Plan, 2 = Checkout. Drives both the rail and the card body.
  const [stage, setStage] = useState(1);
  const isNavigatingRef = useRef(false);

  const scrollToTop = (onComplete) => {
    const startY =
      window.scrollY ||
      document.documentElement?.scrollTop ||
      document.body?.scrollTop ||
      document.querySelector('.shell__main')?.scrollTop ||
      0;

    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    if (document.documentElement) {
      document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
    if (document.body) {
      document.body.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
    const shellMain = document.querySelector('.shell__main');
    if (shellMain) {
      shellMain.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }

    if (onComplete) {
      if (startY <= 20) {
        onComplete();
        return;
      }
      let completed = false;
      const done = () => {
        if (!completed) {
          completed = true;
          onComplete();
        }
      };
      const checkScroll = () => {
        const currentY =
          window.scrollY ||
          document.documentElement?.scrollTop ||
          document.body?.scrollTop ||
          document.querySelector('.shell__main')?.scrollTop ||
          0;
        if (currentY <= 25) {
          done();
        } else if (!completed) {
          requestAnimationFrame(checkScroll);
        }
      };
      requestAnimationFrame(checkScroll);
      setTimeout(done, 350);
    }
  };

  const goToStage = (nextStage) => {
    if (nextStage === stage || isNavigatingRef.current) return;
    if (nextStage > stage) {
      isNavigatingRef.current = true;
      scrollToTop(() => {
        setStage(nextStage);
        isNavigatingRef.current = false;
        scrollToTop();
      });
    } else {
      setStage(nextStage);
      scrollToTop();
    }
  };

  const [stepId, setStepId] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [challengeId, setChallengeId] = useState(null);
  // null until the trader picks one — the default comes from the country below.
  const [platformId, setPlatformId] = useState(null);
  /**
   * Arriving from a coupon card's "Use coupon": the code rides in as
   * `?coupon=`, seeds the field, and the parameter is then dropped from the URL
   * (see below). Seeded through the initialiser rather than an effect so the
   * field is never briefly empty, and so nothing fights the trader if they
   * clear it.
   */
  const [params, setParams] = useSearchParams();
  const couponParam = params.get('coupon');
  const [discount, setDiscount] = useState(() => (couponParam ?? '').trim().toUpperCase());
  /** Scrolled to once, so the pre-filled field is the first thing seen. */
  const discountRef = useRef(null);

  /**
   * "Buy again" on a Payment History line: the plan name and account size of
   * the order being repeated. Applied once the catalogue has loaded — the
   * picker resolves against it, so there is nothing to match until then.
   */
  const planParam = params.get('plan');
  const sizeParam = params.get('size');
  // Payment History's "Buy again": the catalogue's own challenge id, from `buy_again`.
  const challengeParam = params.get('challenge');
  const reordered = useRef(false);
  /**
   * The code that has actually been sent for pricing, as opposed to the one
   * being typed. Apply is what promotes one to the other, so a half-typed code
   * never re-prices the order and the summary's total always matches the
   * coupon named beside it.
   */
  const [appliedCoupon, setAppliedCoupon] = useState('');

  /**
   * Back to no code at all — both the typed one and the applied one, so the
   * field can never show a discount that isn't on the order. Callers: Remove,
   * and a step, account type or size change dropping a coupon that belonged to
   * the old evaluation.
   */
  const clearCoupon = () => {
    setDiscount('');
    setAppliedCoupon('');
  };
  
  useEffect(() => {
    if (!couponParam) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('coupon');
        return next;
      },
      { replace: true },
    );
    // After paint, so the summary is laid out and the field is where it lands.
    const id = requestAnimationFrame(() => {
      discountRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      discountRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
    // Once, for the arrival — `params` changing afterwards is the tidy-up itself.
    // eslint-disable-next-line
  }, []);

  /**
   * The other half of "Buy again": put the picker on the plan and size of the
   * order being repeated.
   *
   * Waits for the catalogue because the selection is resolved against it, and
   * runs once (`reordered`) so it seeds the choice rather than owning it — the
   * trader can change any of it afterwards without this putting it back.
   *
   * Matched on the plan's name and the challenge's balance rather than on ids:
   * Payment History records what was bought, not the catalogue row it came
   * from, and challenge ids are the endpoint's own and are not stable across
   * a re-issue of the plan. A size that is no longer sold still lands on the
   * right plan, with the size left at its default.
   */
  useEffect(() => {
    if (reordered.current || !planParam || !catalogue.length) return;

    const norm = (value) => String(value ?? '').trim().toLowerCase();
    const wanted = norm(planParam);
    const size = Number(sizeParam);
    const hasSize = Number.isFinite(size) && size > 0;

    /*
     * Name and size are matched independently on purpose. Payment History
     * records the plan as it was sold ("Alpha Pro 8% Legacy"), which is not
     * always the catalogue's `plan_type` ("Alpha pro") — and when the name
     * missed, requiring it first threw the size away too and dropped the whole
     * thing on the catalogue's default, the cheapest challenge on the list.
     *
     * So: a plan whose name matches loosely (either way round, since one side
     * is a prefix of the other when it carries a variant), and within it the
     * requested size, or failing that the nearest size that plan sells.
     */
    const nameMatches = (label) => {
      const l = norm(label);
      return Boolean(wanted) && (l === wanted || l.startsWith(wanted) || wanted.startsWith(l));
    };
    const nearest = (challenges) =>
      hasSize
        ? challenges.reduce((best, ch) =>
            Math.abs(ch.balance - size) < Math.abs(best.balance - size) ? ch : best,
          )
        : null;

    let exact = null;    // right plan, right size
    let byPlan = null;   // right plan, nearest size it sells
    let bySize = null;   // that size, under some other plan name

    // The exact challenge, when Payment History named it — no guessing needed.
    if (challengeParam) {
      for (const s of catalogue) {
        for (const p of s.plans) {
          const ch = p.challenges.find((c) => String(c.id) === String(challengeParam));
          if (ch) exact = { step: s, plan: p, challenge: ch };
        }
      }
    }

    if (!exact) for (const s of catalogue) {
      for (const p of s.plans) {
        const named = nameMatches(p.label);
        const sized = hasSize ? p.challenges.find((ch) => ch.balance === size) : null;

        if (named && sized) { exact = { step: s, plan: p, challenge: sized }; break; }
        if (named && !byPlan) byPlan = { step: s, plan: p, challenge: nearest(p.challenges) };
        if (sized && !bySize) bySize = { step: s, plan: p, challenge: sized };
      }
      if (exact) break;
    }

    // The plan identifies the order, so it outranks a size found elsewhere.
    const hit = exact ?? byPlan ?? bySize;

    reordered.current = true;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('plan');
        next.delete('size');
        next.delete('challenge');
        return next;
      },
      { replace: true },
    );

    /*
     * Say so whenever the order could not be repeated as it was. A 1K or 75K
     * order silently becoming the cheapest challenge on the list is how
     * somebody buys the wrong thing without ever being told the original was
     * withdrawn — so every inexact landing is announced, and a landing that
     * matched nothing at all changes no selection at all.
     */
    if (!hit) {
      toast.warning(
        t('That plan is no longer sold'),
        t('Choose an evaluation below to carry on.'),
      );
      return;
    }

    setStepId(hit.step.id);
    setPlanId(hit.plan.id);
    if (hit.challenge) setChallengeId(hit.challenge.id);

    if (!exact) {
      const asked = hasSize ? formatAccountSize(size) : null;
      const got = hit.challenge ? formatAccountSize(hit.challenge.balance) : null;
      // Landed on a different plan: the plan is what was retired, not the size
      // (TI review E8 — e.g. Alpha Three), so the title names the plan.
      const planRetired = hit === bySize;
      toast.warning(
        planRetired
          ? t('{{plan}} is no longer sold', { plan: planParam })
          : asked
            ? t('The {{size}} size is no longer sold', { size: asked })
            : t('That order is no longer sold as it was'),
        planRetired
          ? got
            ? t(
                '{{plan}} at {{size}} is selected instead — change it below if you want a different evaluation.',
                { plan: hit.plan.label, size: got },
              )
            : t('{{plan}} is selected instead — change it below if you want a different evaluation.', {
                plan: hit.plan.label,
              })
          : got
            ? t(
                '{{plan}} at {{size}} is selected instead — change it below if you need a different size.',
                { plan: hit.plan.label, size: got },
              )
            : t('{{plan}} is selected — pick a size below.', { plan: hit.plan.label }),
      );
    }
  }, [catalogue, planParam, sizeParam, challengeParam, setParams]);

  // Nuvei is the one card provider on offer — see `paymentMethods` (N03).
  const [payment, setPayment] = useState('nuvei');
  // CoinPayments is only selected once the trader proceeds past its warning.
  const [cryptoWarningOpen, setCryptoWarningOpen] = useState(false);
  const choosePayment = (id) => {
    if (id === 'coinspayment' && payment !== id) setCryptoWarningOpen(true);
    else setPayment(id);
  };
  const [spread, setSpread] = useState('standard');
  // On demand is the default on every plan, including the ones that also offer
  // bi-weekly — it is the cheaper of the two wherever the prices differ.
  const [payoutId, setPayoutId] = useState('ondemand');
  const [eaId, setEaId] = useState('no');
  const [addOnIds, setAddOnIds] = useState([]);
  const [edits, setEdits] = useState({});
  const [errors, setErrors] = useState({});
  /** Ticked consents, keyed by id — every one shown is required to pay. */
  const [consented, setConsented] = useState({});
  /** "Show Objectives" — the plan rules in the Order Summary, on both screens. */
  const [showObjectives, setShowObjectives] = useState(false);

  // The Purchase Review dialog holds the server's own pricing of the order: it
  // opens only once `/payment/account-preview/` has answered, so it can never
  // show a number the backend hasn't agreed to.
  const [preview, setPreview] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [previewAccount, { isLoading: isPreviewing }] = usePreviewAccountMutation();
  const [createPaymentUrl, { isLoading: isRedirecting }] = useCreatePaymentUrlMutation();
  const [createFreeTrial, { isLoading: isCreatingTrial }] = useCreateFreeTrialMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * Each level falls back to the first option instead of being seeded by an
   * effect: that both defaults the selection when the catalogue arrives and
   * re-defaults the levels below whenever one above changes, with no effect to
   * keep in sync.
   */
  const step = catalogue.find((s) => s.id === stepId) ?? catalogue[0] ?? null;
  const plan = step?.plans.find((p) => p.id === planId) ?? step?.plans[0] ?? null;
  const challenge =
    plan?.challenges.find((c) => c.id === challengeId) ?? plan?.challenges[0] ?? null;

  /*
   * A coupon belongs to the evaluation it was applied to, so a new step, account
   * type or size starts without one — AC1-I84 for the step, and the same for the
   * other two, where the code used to linger and re-check against the new plan
   * as "Invalid coupon". The typed code goes with it, so the field can't sit
   * there showing a discount that is no longer on the order.
   *
   * Each compares against the resolved selection rather than its raw id, which
   * is null until the trader picks one: re-clicking the tile already selected
   * must not throw away a code they have just entered.
   */
  const selectStep = (id) => {
    if (id !== step?.id) clearCoupon();
    setStepId(id);
    // Plan types and sizes both differ per step, so let them re-default.
    setPlanId(null);
    setChallengeId(null);
  };

  const selectPlan = (id) => {
    if (id !== plan?.id) clearCoupon();
    // Free Trial takes no Expert Advisor: the live checkout answers EA Use "No"
    // for it on the way in (N12), so leaving it doesn't bring back a "Yes".
    if (id.toLowerCase() === 'free trial') setEaId('no');
    setPlanId(id);
    setChallengeId(null);
  };

  const selectChallenge = (id) => {
    if (id !== challenge?.id) clearCoupon();
    setChallengeId(id);
  };

  // ---- plan-dependent rules, ported from the old checkout ----

  const planType = (plan?.label ?? '').toLowerCase();
  const isFreeTrial = planType === 'free trial';

  /**
   * Platform availability, ported from the old checkout's Platform block:
   *
   * - the $2,500 account is MetaTrader-5 only, and the others aren't listed at
   *   all (its `is2500Challenge` filter drops them from the list)
   * - Free Trial still renders every tile but disables all but MT5
   * - Alpha Trader is disabled everywhere
   *
   * Resolving the selection against that list instead of storing it also fixes a
   * bug in the old page: it filters the tiles for the $2,500 account but never
   * clears `selectedPlatform`, so arriving there from a cTrader selection leaves
   * cTrader submitted while only MT5 is on screen.
   */
  const platformOptions =
    challenge?.balance === 2500 ? platforms.filter((p) => p.id === 'mt5') : platforms;

  const platformOff = (option) =>
    Boolean(option.disabled) || (isFreeTrial && option.id !== 'mt5');

  /*
   * Until the trader picks a platform, a US client starts on cTrader and
   * everyone else on MetaTrader 5, as on the live checkout. A default rather
   * than a pick, so it follows the country if that answers after first paint,
   * and the rules above still win: Free Trial and the $2,500 account fall back
   * to MT5 for a US client too.
   */
  const picked = platformOptions.find(
    (p) => p.id === (platformId ?? (isUsClient ? 'ctrader' : platforms[0].id)),
  );
  const platform =
    picked && !platformOff(picked)
      ? picked
      : platformOptions.find((p) => !platformOff(p)) ?? platformOptions[0];

  /**
   * The consents this platform and client are asked for — the US-person one is
   * dropped on DX Trade and TradeLocker (AC1-I110) and for a client in the US,
   * but asked on cTrader (see `consents`). Resolved, not reset: a tick on a consent
   * that stops showing stays in `consented` but no longer counts, and comes
   * back if the consent does.
   */
  const shownConsents = consents.filter(
    (c) => !c.hiddenOn?.includes(platform.id) && !c.hiddenFor?.includes(geo?.country),
  );
  const allConsented = shownConsents.every((c) => consented[c.id]);

  const toggleConsent = (id) => {
    const next = { ...consented, [id]: !consented[id] };
    setConsented(next);
    // One message stands for the whole set, so it clears when the set is
    // complete rather than on the first tick with two still missing.
    if (errors.consent && shownConsents.every((c) => next[c.id])) {
      setErrors((prev) => ({ ...prev, consent: undefined }));
    }
  };

  /**
   * Payout Type is a real choice only where the challenge is natively bi-weekly
   * — `payout_type: 1`. On the rest both prices are identical, so the section
   * doesn't draw. Free Trial carries the same 1 but is free under either, and
   * the page already skips Payment Method and Add-ons there for the same
   * reason: nothing is being bought, so there is nothing to configure.
   * That leaves all 18 Alpha Pro sizes — 6%, 8% and, since AC1-I87 stopped
   * filtering on `publish`, 10%.
   *
   * Resolved against that rather than reset in an effect, the way `platform` and
   * `effectiveSpread` are: moving to a plan without the choice simply stops the
   * selection counting, and moving back restores it.
   */
  const showPayoutType = !isFreeTrial && challenge?.payoutType === PAYOUT_TYPE_BI_WEEKLY;
  const payout =
    (showPayoutType && payoutTypes.find((p) => p.id === payoutId)) || ON_DEMAND_PAYOUT;
  const onDemand = payout.id === 'ondemand';

  /**
   * Whether this plan can be upgraded to a 90% split at all. The old checkout
   * gates it on step and plan: Step 1 always, Step 2 only for Alpha Pro, Step 3
   * for anything — the last two also requiring an on-demand payout.
   *
   * That last clause now bites: bi-weekly has no 90% price in the row and
   * `/final-price/` quotes `profit_split=90` at the 80 figure on that branch, so
   * offering it there would be a free upgrade the backend never charges for.
   * Step 1 is exempt because its plans are on-demand anyway.
   */
  const showProfitSplit =
    step?.label === 'Step 1' ||
    (onDemand &&
      ((step?.label === 'Step 2' && planType.includes('alpha pro')) ||
        step?.label === 'Step 3'));

  /**
   * Expert Advisors are a MetaTrader feature, so only MetaTrader is asked — the
   * same gate Swap-Free already uses, and MetaTrader 5 is the only MetaTrader
   * tile the new design ships. Alpha Direct isn't asked at all (AC1-I111), the
   * same way it gets no add-ons.
   *
   * Moving to Alpha Direct takes the answer off the order and moving back
   * restores it. Leaving MT5 is different: `selectPlatform` resets the answer to
   * "No", as the live checkout does, so returning to MT5 never brings back a
   * "Yes". `null` is what `orderBody` drops, so an order without the question
   * posts no `EAs` field at all — which is what every order does today.
   */
  const showEaUse = platform.id === 'mt5' && !NO_EA_PLANS.includes(planType);
  // On Free Trial the question is shown but locked on "No", as on the live
  // checkout (N12) — resolved here too, so nothing else can put a "Yes" on it.
  const eaChoice = showEaUse
    ? (isFreeTrial ? null : eaChoices.find((c) => c.id === eaId)) ?? eaChoices[0]
    : null;
  const eaOn = Boolean(eaChoice?.value);

  /**
   * Swap-Free is MetaTrader-5 only and never offered on Swing or Alpha Direct.
   * Filtering here rather than clearing the selection in an effect means an
   * add-on that stops being available simply stops counting — change platform
   * away from MT5 and the swap-free choice drops out of the order by itself.
   */
  const visibleAddOns = NO_ADDON_PLANS.includes(planType)
    ? []
    : addOns.filter((addOn) => {
        if (addOn.kind === 'profit-split') return showProfitSplit;
        if (addOn.kind === 'swap-free') {
          return platform.id === 'mt5' && !NO_SWAP_FREE_PLANS.includes(planType);
        }
        return true;
      });

  /**
   * A trader running an Expert Advisor can't take a swap-free account — AC1-I112.
   * The two lock each other, as on the live checkout: EA Use "Yes" can't be
   * picked while Swap-Free is ticked, and Swap-Free can't be picked while EA Use
   * is "Yes". Neither answer ever adds or drops the other behind the trader's
   * back — Swap-Free is a paid extra, so it only goes on the order when ticked.
   */
  const addOnOff = (addOn) => addOn.kind === 'swap-free' && eaOn;

  const activeAddOns = visibleAddOns.filter((a) => addOnIds.includes(a.id) && !addOnOff(a));
  const swapFreeOn = activeAddOns.some((a) => a.kind === 'swap-free');
  const ninetySplitOn = activeAddOns.some((a) => a.kind === 'profit-split');
  /** Why EA "Yes" can't be picked right now, or null when it can. */
  const eaYesBlocked = isFreeTrial
    ? 'Not available on a Free Trial'
    : swapFreeOn
      ? 'Not available with a swap-free account'
      : null;
  const eaChoiceOff = (option) => option.value && Boolean(eaYesBlocked);

  /**
   * A plan's rules at the size the trader is on — or its smallest, where the
   * plan doesn't sell that size — as the live checkout's Account Type tiles
   * list them. Only the selected plan reflects the trader's own choices (the 90%
   * add-on, a bi-weekly payout); the others are quoted as sold.
   */
  const objectivesFor = (option) => {
    const c = option.challenges.find((x) => x.balance === challenge?.balance) ?? option.challenges[0];
    const isSelected = option.id === plan?.id;
    return planObjectives({
      row: c.row,
      balance: c.balance,
      planType: option.label.toLowerCase(),
      stepCount: c.stepCount,
      profitSplit: isSelected && ninetySplitOn ? 90 : Number(c.row.Profit_Split) || 0,
      payoutLabel: isSelected ? payout.label : ON_DEMAND_PAYOUT.label,
    });
  };
  const objectives = plan ? objectivesFor(plan) : [];

  /**
   * Ticking Swap-Free moves the spread to Standard for good, as on the live
   * checkout (N90): unticking it leaves Standard selected rather than bringing
   * back a Raw spread the trader gave up — Raw is the trader's to pick again.
   */
  const toggleAddOn = (id) => {
    const adding = !addOnIds.includes(id);
    if (adding && addOns.find((a) => a.id === id)?.kind === 'swap-free') setSpread('standard');
    setAddOnIds((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  /**
   * "Yes" is locked while Swap-Free is on the order, but a Swing plan asks EA
   * Use without offering Swap-Free, so a tick made on another plan can still be
   * sitting in `addOnIds` out of sight. Answering "Yes" drops it for good, so it
   * can't reappear on the order when the trader moves back to a plan that offers it.
   */
  const dropSwapFree = () =>
    setAddOnIds((prev) => prev.filter((id) => addOns.find((a) => a.id === id)?.kind !== 'swap-free'));

  const chooseEa = (option) => {
    if (option.value) dropSwapFree();
    setEaId(option.id);
  };

  /**
   * Swap-Free is a paid extra, so it doesn't survive a platform change: leaving
   * MT5 hides it, and coming back must not put it on the order again without a
   * fresh tick — as on the live checkout.
   */
  const selectPlatform = (id) => {
    if (id !== platform.id) {
      dropSwapFree();
      // EA Use is only asked on MT5, and the live checkout resets the answer to
      // "No" on leaving it — coming back asks afresh rather than restoring "Yes".
      if (id !== 'mt5') setEaId('no');
    }
    setPlatformId(id);
  };

  // A swap-free account can only run on the standard spread. `toggleAddOn`
  // already moves `spread` there; this keeps the order right regardless.
  const effectiveSpread = swapFreeOn ? 'standard' : spread;

  /* One travelling highlight per group of choices — see useSelectionFill.jsx.
     Read-only: each call takes the id that is already selected and the length
     of the list it sits in, and hands back a ref, a class and a decorative
     span. Nothing below changes what a choice does, only how the marker gets
     to it.

     The second argument is the group's *layout*: when it changes the tiles
     themselves have changed, and the marker is placed rather than slid. Sizes
     and types re-key on their list length, which is what changes when another
     Step re-defaults them.

     Add-ons is absent on purpose — it is multi-select, and one marker cannot
     mark two tiles. It keeps the per-tile crossfade. */
  const stepFill = useSelectionFill(step?.id, catalogue.length);
  const planFill = useSelectionFill(plan?.id, step?.plans.length);
  const sizeFill = useSelectionFill(challenge?.id, plan?.challenges.length);
  const platformFill = useSelectionFill(platform?.id, platformOptions.length);
  const paymentFill = useSelectionFill(payment, paymentMethods.length);
  const spreadFill = useSelectionFill(effectiveSpread, spreads.length);
  const payoutFill = useSelectionFill(payout.id, payoutTypes.length);
  const eaFill = useSelectionFill(eaChoice?.id, eaChoices.length);

  /**
   * What the 90% split adds. The old checkout doesn't compute this — it posts
   * `profit_split` and reads the price back from the server — so this is the
   * closest local stand-in: each challenge's own `ninty_pfs_price`.
   *
   * The Alpha Pro rows that used to come out negative here were the scheduled
   * `Price` being subtracted from an on-demand `ninty_pfs_price`, not a quirk of
   * the data; against the on-demand base not one of the 52 rows is below it. The
   * clamp stays only so a future bad row reads as no fee rather than "+-$3.00".
   */
  const ninetySplitFee = challenge
    ? Math.max(0, challenge.ninetySplitPrice - challenge.price.ondemand)
    : 0;

  // Whichever payout type is in force — the two differ on Alpha Pro 6% and 8%.
  const basePrice = challenge ? challenge.price[payout.id] : null;

  /**
   * The real price, re-fetched whenever any of its inputs change — RTK Query
   * keys its cache on this argument, so changing the size, the platform, the
   * split, the swap-free add-on, or the applied coupon is the auto-call.
   *
   * Two parameters are left `undefined` rather than defaulted, matching what
   * the live checkout puts on the wire: `platform` is only sent off MetaTrader
   * 5, and `profit_split` only where the plan offers the 90% upgrade at all.
   */
  const priceArgs = {
    challengeId: challenge?.id,
    ondemand: onDemand,
    platform: platform.id === 'mt5' ? undefined : platform.id,
    profit_split: showProfitSplit ? (ninetySplitOn ? 90 : 80) : undefined,
  };
  const pricing = useGetFinalPriceQuery(
    { ...priceArgs, swap_free: swapFreeOn, coupon: appliedCoupon || undefined },
    { skip: !challenge },
  );

  /**
   * What Swap-Free adds — N08. The challenge row carries a 90% split price but
   * no swap-free one; the back end works it out (about a tenth of the price,
   * rounded: $397 → $437 on Alpha One 6% $100K, $67 → $74 at $10K), so the fee
   * is read off the endpoint itself: this order priced with and without it,
   * before any coupon. Only fetched where the add-on is on offer, and shown
   * only once both answers are for this size.
   *
   * Priced at the 80% split whatever the trader has picked: the fee is the same
   * either way (+$4 on Alpha Pro 10% $5K at 80% and at 90%, and so on across
   * the plans), and holding the split still means ticking 90% doesn't refetch
   * it — which is what lets the local total below already include it while
   * the order's own price is on its way (N13). With 80% and no coupon one of
   * the two is the order's own query, so RTK Query answers it from cache.
   */
  const swapFreeOffered = visibleAddOns.some((a) => a.kind === 'swap-free');
  const swapFreeArgs = { ...priceArgs, profit_split: showProfitSplit ? 80 : undefined };
  const withSwapFree = useGetFinalPriceQuery(
    { ...swapFreeArgs, swap_free: true },
    { skip: !challenge || !swapFreeOffered },
  );
  const withoutSwapFree = useGetFinalPriceQuery(
    { ...swapFreeArgs, swap_free: false },
    { skip: !challenge || !swapFreeOffered },
  );
  const swapFreeFee =
    withSwapFree.currentData !== undefined && withoutSwapFree.currentData !== undefined
      ? Math.max(
          0,
          (finalPriceFrom(withSwapFree.currentData) ?? 0) -
            (finalPriceFrom(withoutSwapFree.currentData) ?? 0),
        )
      : 0;

  /** The one-time fee each add-on card quotes, or 0 for none to show. */
  const addOnFee = (addOn) =>
    addOn.kind === 'profit-split' ? ninetySplitFee : addOn.kind === 'swap-free' ? swapFreeFee : 0;

  /**
   * `currentData`, not `data`. RTK Query's `data` is "the latest result
   * regardless of hook arg" — it holds the *previous* selection's answer while
   * the new one is in flight, so reading it quotes the old size's price, or the
   * price from before the coupon, for the length of a round trip.
   * `currentData` is empty until the answer for these arguments arrives, which
   * is what puts the summary back on its local estimate in the meantime.
   */
  const serverTotal =
    pricing.currentData === undefined ? null : finalPriceFrom(pricing.currentData);

  // The local figure is only a placeholder for the round trip: it is the same
  // arithmetic the size and add-on cards already show, so nothing new appears
  // while the server is answering. With both fees in it, it is the server's own
  // figure (checked against `/final-price/` on every Step 1 and Step 2 plan), so
  // ticking add-ons quickly no longer flashes a total in between — N13; before,
  // Alpha Pro 10% $5K read $36 on its way to $40.
  const localTotal = challenge
    ? basePrice + (ninetySplitOn ? ninetySplitFee : 0) + (swapFreeOn ? swapFreeFee : 0)
    : null;
  const orderTotal = serverTotal ?? localTotal;

  /**
   * Whether the applied code did anything. A code the backend doesn't know is
   * a 400 (`{"detail":"Invalid coupon"}`), so the error is the usual signal;
   * `coupon_applied` carries the matched coupon's id on success and is null
   * when nothing matched, which covers any code it accepts but ignores.
   */
  /**
   * No verdict until there is one. `isFetching` holds the note back while the
   * request is out, and `currentData` is this argument's answer rather than the
   * last one for any argument — together they stop the pre-coupon response
   * (`coupon_applied: null`, because no coupon was sent) from being read as a
   * rejection of the code now being checked, which flashed "Invalid coupon
   * code." under the spinner before the server had spoken.
   */
  const couponRejected =
    Boolean(appliedCoupon) &&
    !pricing.isFetching &&
    (Boolean(pricing.error) ||
      (pricing.currentData !== undefined && !pricing.currentData.coupon_applied));

  // Why, in the endpoint's own words — AC1-I82. "Invalid coupon" and "This
  // coupon is only applicable to Alpha Direct plans." are different problems
  // with different fixes, and the trader can only act on the second if we say
  // it. The 200-with-no-coupon path carries no message, hence the fallback.
  const couponError = couponRejected ? apiMessage(pricing.error, COUPON_INVALID) : null;

  const applyCoupon = () => setAppliedCoupon(discount.trim());

  /**
   * Typing in the field after a code was turned down drops that code — N14.
   * The verdict is about the code that was sent, so once the field no longer
   * holds it (cleared, or being corrected) "Invalid coupon" would describe
   * something that isn't there. A rejected code was never on the order, so
   * nothing about the total changes. The field is locked while a code is
   * accepted, so this never touches one that is.
   */
  const editCoupon = (value) => {
    setDiscount(value);
    if (couponRejected) setAppliedCoupon('');
  };

  /**
   * A code the server has accepted is settled: the field locks so it can't drift
   * away from the total beside it — AC1-I83. A rejected one unlocks again, for
   * the trader to correct.
   *
   * Deliberately not gated on `isFetching`: a code stays locked while it is
   * being re-checked, so changing the size or an add-on doesn't flicker the
   * field editable and back for the length of every round trip.
   */
  const couponLocked = Boolean(appliedCoupon) && !couponRejected;

  /**
   * What the accepted code takes off — N11. The saving is read as the gap
   * between the endpoint's own `actual_price` and `discounted_price` rather
   * than from its `discount` field, so the line always adds up to the total
   * beside it. Only off this argument's answer (`currentData`), so a size or
   * add-on change drops the line until the new price is in, like the total.
   */
  const pricedOrder = couponLocked ? pricing.currentData : undefined;
  const originalPrice = Number(pricedOrder?.actual_price);
  const couponSaving =
    pricedOrder && Number.isFinite(originalPrice)
      ? Math.max(0, originalPrice - Number(pricedOrder.discounted_price))
      : 0;

  // ---- customer information ----

  /**
   * Prefilled from the trader's profile record and locked, the way the old
   * checkout disables any field the user record already answers. Whatever the
   * record lacks stays editable, and is saved back to it on purchase.
   *
   * The session's name and email sit underneath as a first paint, so the form
   * isn't blank while the profile call is in flight; they don't lock anything,
   * since only the record decides what is already answered.
   */
  const { data: profile } = useGetProfileDetailsQuery();
  const [updateProfile] = useUpdateProfileDetailsMutation();

  const answers = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(EMPTY_CUSTOMER)
          .filter((key) => profile?.[key])
          .map((key) => [key, profile[key]]),
      ),
    [profile],
  );

  const sessionFill = useMemo(() => {
    const [first, ...rest] = (user?.name ?? '').trim().split(/\s+/).filter(Boolean);
    return Object.fromEntries(
      Object.entries({ first_name: first, last_name: rest.join(' '), email: user?.email }).filter(
        ([, value]) => value,
      ),
    );
  }, [user]);

  const customer = { ...EMPTY_CUSTOMER, ...sessionFill, ...answers, ...edits };
  const locked = (name) => Boolean(answers[name]);

  const editCustomer = (e) => {
    const { name, value } = e.target;
    setEdits((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  /**
   * The order as it goes on the wire, derived from the selection rather than
   * stored: an add-on that stops being available stops being ordered, for the
   * same reason `activeAddOns` is filtered rather than cleared.
   */
  const order = () =>
    orderBody({
      refreshToken,
      paymentMethod: payment,
      challengeId: challenge.id,
      coupon: appliedCoupon,
      customer,
      // The dial code rides in front of the number, digits only — 44 + phone.
      contact: `${customer.country_code}${customer.contact_number}`.replace(/\D/g, ''),
      platform: platform.id,
      rawSpread: effectiveSpread === 'raw',
      swapFree: swapFreeOn,
      // Alpha Direct is sold at a 90% split; the rest at 80 unless upgraded.
      profitSplit: ninetySplitOn || planType.includes('alpha direct') ? 90 : 80,
      payoutType: payout.value,
      eaUse: eaChoice ? eaChoice.value : null,
    });

  /**
   * Validate, then price the order server side. Nothing is charged here — the
   * preview only opens the review dialog, and Confirm is what mints a payment
   * URL.
   */
  /**
   * Complete Purchase is disabled until every consent shown is ticked (TI
   * review N10), so its state matches the check below. The check stays as a
   * guard, and if it ever trips, the first unticked box — in the Order Summary,
   * which can be off-screen or below the fold on a phone — is brought into
   * view and focused.
   */
  const consentsRef = useRef(null);
  const revealConsents = () => {
    const box = consentsRef.current?.querySelector('input[type="checkbox"]:not(:checked)');
    if (!box) return;
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    box.focus({ preventScroll: true });
  };

  const completePurchase = async () => {
    // Free Trial needs no processor — the old checkout skips the same check.
    if (!payment && !isFreeTrial) {
      setErrors({ payment: 'Please select a payment method.' });
      return;
    }

    // Consents are checked alongside the fields rather than after them, so a
    // trader with both outstanding sees both at once instead of fixing the form
    // only to be stopped again by the checkboxes.
    try {
      await customerSchema.validate(customer, { abortEarly: false });
      if (!allConsented) {
        setErrors({ consent: CONSENT_REQUIRED });
        revealConsents();
        return;
      }
      setErrors({});
    } catch (err) {
      const next = {};
      err.inner?.forEach((issue) => {
        if (issue.path && !next[issue.path]) next[issue.path] = issue.message;
      });
      if (!allConsented) next.consent = CONSENT_REQUIRED;
      setErrors(next);
      return;
    }

    // Whatever the profile lacked and the trader filled in here goes back to
    // it, so the next checkout finds it answered. Only once the record has
    // loaded — without it every field reads as a gap — and never as a gate on
    // the purchase: the order carries these details itself, so a failed save
    // is worth a warning, not a blocked checkout.
    const gaps = profile
      ? Object.fromEntries(
          PROFILE_WRITABLE.filter((key) => !answers[key]).map((key) => [
            key,
            String(customer[key]).trim(),
          ]),
        )
      : {};
    // The dial code belongs to the number, so it goes up whenever the number does.
    if (gaps.contact_number) gaps.country_code = customer.country_code;
    if (Object.keys(gaps).length) {
      try {
        await updateProfile(gaps).unwrap();
      } catch (err) {
        toast.warning(
          t('Profile not updated'),
          t(apiMessage(err, 'Your details were not saved to your profile.')),
        );
      }
    }

    setSubmitError(null);
    try {
      setPreview(await previewAccount(order()).unwrap());
    } catch (err) {
      // No dialog for a preview that never arrived — the message belongs next
      // to the button that was pressed.
      setErrors({ submit: checkoutError(err) });
    }
  };

  /**
   * Confirmed: swap the reviewed order for the processor's hosted checkout and
   * hand the tab over. `assign` rather than `replace` so the browser's Back
   * still returns to the configured checkout if the trader abandons payment.
   */
  const confirmPurchase = async () => {
    setSubmitError(null);
    if (isFreeTrial) {
      try {
        const body = order();
        const result = await createFreeTrial({
          challenge: body.challenge_id,
          raw_spread: Boolean(body.raw_spread),
          platform: body.platform,
          EAs: Boolean(body.EAs),
          email: body.email,
        }).unwrap();
        // The endpoint can answer 200 with `success: false`, as the old checkout handled.
        if (!result?.success || !result.login) {
          setSubmitError(apiMessage({ data: result }, CHECKOUT_FAILED));
          return;
        }
        dispatch(selectAccount({ loginId: result.login, platform: body.platform }));
        navigate(
          `/account-metrics?login=${encodeURIComponent(result.login)}` +
            `&platform=${encodeURIComponent(body.platform)}`,
        );
      } catch (err) {
        setSubmitError(checkoutError(err));
      }
      return;
    }
    try {
      const url = paymentUrlFrom(await createPaymentUrl(toFormData(order())).unwrap());
      if (!url) {
        setSubmitError(CHECKOUT_FAILED);
        return;
      }
      window.location.assign(url);
    } catch (err) {
      setSubmitError(checkoutError(err));
    }
  };

  const closeReview = () => {
    setPreview(null);
    setSubmitError(null);
  };

  // Order Summary, 1:12510 — every row reads straight off the selection above.
  const summaryRows = [
    { label: t('Step'), value: step ? t(step.label) : '—' },
    { label: t('Account'), value: plan?.label ?? '—' },
    { label: t('Size'), value: challenge ? usd(challenge.balance) : '—' },
    { label: t('Platform'), value: platform.short },
    ...(stage === 2
      ? [{ label: t('Spread'), value: effectiveSpread === 'raw' ? t('Raw') : t('Standard') }]
      : []),
    // Same rule as Spread: only once the trader is on the step that chooses it,
    // and only where there is a choice to report.
    ...(stage === 2 && showPayoutType ? [{ label: t('Payout'), value: t(payout.label) }] : []),
    // No stage gate, unlike Spread and Payout: this one is answered on step 1
    // alongside Platform, so it can be reported as soon as it is asked.
    ...(eaChoice ? [{ label: t('EA Use'), value: t(eaChoice.label) }] : []),
    // Only shown once it can affect the total, so the number below is explained.
    ...(activeAddOns.length
      ? [{ label: t('Add-ons'), value: activeAddOns.map((a) => t(a.label)).join(', ') }]
      : []),
    // The price before the code and what the code took off, so the total below
    // is explained — N11. Only while an accepted code is actually saving money.
    ...(couponSaving > 0
      ? [
          { label: t('Price'), value: usd(originalPrice, { decimals: 2 }) },
          {
            label: t('Discount ({{code}})', { code: appliedCoupon }),
            value: usd(-couponSaving, { decimals: 2 }),
            tone: 'discount',
          },
        ]
      : []),
  ];

  return (
    <div className="trading-assessment">
      <header className="ta-hero">
        <span className="ta-hero__chip">{t('Checkout')}</span>
        <h1 className="ta-hero__title">{t('Choose Your Evaluation')}</h1>
      </header>

      <div className="ta-layout">
        <section className="ta-card">
          {/* Progress rail, 1:12259 */}
          <div className="ta-steps">
            {checkoutSteps.map((railStep, i) => {
              const isCurrent = railStep.n === stage;
              const isDone = railStep.n < stage;
              const isTodo = railStep.n > stage;
              return (
                <Fragment key={railStep.n}>
                  {i > 0 && <span className="ta-steps__line" />}
                  <div
                    className={`ta-steps__item${isTodo ? ' ta-steps__item--todo' : ''}${
                      isDone ? ' ta-steps__item--done' : ''
                    }`}
                    onClick={() => {
                      if (isDone) goToStage(railStep.n);
                    }}
                    role={isDone ? 'button' : undefined}
                    tabIndex={isDone ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (isDone && (e.key === 'Enter' || e.key === ' ')) {
                        goToStage(railStep.n);
                      }
                    }}
                  >
                    <span
                      className={`ta-steps__badge${isCurrent ? ' ta-steps__badge--on' : ''}`}
                    >
                      {railStep.n}
                    </span>
                    <span className="ta-steps__text">
                      <span className="ta-steps__label">{t(railStep.label)}</span>
                      <span className="ta-steps__sub">{t(railStep.sub)}</span>
                    </span>
                  </div>
                </Fragment>
              );
            })}
          </div>

          <span className="ta-rule" />

          {isLoading ? (
            <CheckoutSkeleton />
          ) : !challenge ? (
            <div className="ta-state">
              <span>
                {isError ? t('We could not load the plans.') : t('No plans are available right now.')}
              </span>
              <button type="button" className="ta-state__retry" onClick={refetch}>
                {t('Try again')}
              </button>
            </div>
          ) : stage === 1 ? (
            <div className="ta-sections">
              {/* Choose your Step, 1:12277 */}
              <section className="ta-section">
                <div className="ta-section__head">
                  {/* The switch sits where the live checkout puts it. */}
                  <div className="ta-section__title-row">
                    <h2 className="ta-section__title">{t('Choose your Step')}</h2>
                    <ObjectivesSwitch
                      on={showObjectives}
                      onToggle={() => setShowObjectives((on) => !on)}
                    />
                  </div>
                  <p className="ta-section__desc">
                    {t('Choose your preferred funding process and continue through the setup in a few simple steps.')}
                  </p>
                </div>

                <div
                  className={`ta-plans${stepFill.className}`}
                  ref={stepFill.ref}
                  style={cols(catalogue.length)}
                >
                  {stepFill.node}
                  {catalogue.map((option, i) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`ta-plan${option.id === step.id ? ' ta-plan--on' : ''}`}
                      aria-pressed={option.id === step.id}
                      onClick={() => selectStep(option.id)}
                    >
                      <span className="ta-plan__tile">
                        <span
                          className="ta-plan__glyph"
                          style={icon(stepIcons[i] ?? stepIcons[stepIcons.length - 1])}
                        />
                      </span>
                      <span className="ta-plan__text">
                        <span className="ta-plan__label">{t(option.label)}</span>
                        <span className="ta-plan__note">
                          {stepNote(option.plans[0].challenges[0].stepCount)}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>

              <span className="ta-rule" />

              {/* Account Type, 1:12313 */}
              <section className="ta-section">
                <div className="ta-section__head">
                  <h2 className="ta-section__title">{t('Account Type')}</h2>
                  <p className="ta-section__desc">
                    {t('Select the trading account that best matches your strategy, goals, and trading style.')}
                  </p>
                </div>

                <div
                  className={`ta-types${planFill.className}`}
                  ref={planFill.ref}
                  style={cols(step.plans.length)}
                >
                  {planFill.node}
                  {step.plans.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`ta-type ta-type--rules${option.id === plan.id ? ' ta-type--on' : ''}`}
                      aria-pressed={option.id === plan.id}
                      onClick={() => selectPlan(option.id)}
                    >
                      <Radio on={option.id === plan.id} />
                      <span className="ta-type__label">{option.label}</span>
                      {/* Every tile, not just the selected one: the point of
                          the switch is comparing plans before choosing. */}
                      <ObjectivesList rows={objectivesFor(option)} open={showObjectives} />
                    </button>
                  ))}
                </div>
              </section>

              <span className="ta-rule" />

              {/* Account Size, 1:12331 */}
              <section className="ta-section">
                <div className="ta-section__head">
                  <h2 className="ta-section__title">{t('Account Size')}</h2>
                  <p className="ta-section__desc">
                    {t('Pick the account balance you want to trade with based on your experience and risk preference.')}
                  </p>
                </div>

                <div
                  className={`ta-sizes${sizeFill.className}`}
                  ref={sizeFill.ref}
                  style={cols(plan.challenges.length, 6)}
                >
                  {sizeFill.node}
                  {plan.challenges.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`ta-size${option.id === challenge.id ? ' ta-size--on' : ''}`}
                      aria-pressed={option.id === challenge.id}
                      onClick={() => selectChallenge(option.id)}
                    >
                      <span className="ta-size__head">
                        <Radio on={option.id === challenge.id} />
                        <span className="ta-size__label">{usd(option.balance)}</span>
                      </span>
                      <span className="ta-size__rule" />
                      <span className="ta-size__fee">
                        {usd(option.price[payout.id], { decimals: 2 })}
                      </span>
                      <span className="ta-size__note">{t('One time pay')}</span>
                    </button>
                  ))}
                </div>
              </section>

              <span className="ta-rule" />

              {/* Platform, 1:12445 */}
              <section className="ta-section">
                <div className="ta-section__head">
                  <h2 className="ta-section__title">{t('Platform')}</h2>
                  <p className="ta-section__desc">
                    {t('Choose the trading platform you are most comfortable using for execution and analysis.')}
                  </p>
                </div>

                <div className={`ta-platforms${platformFill.className}`} ref={platformFill.ref}>
                  {platformFill.node}
                  {platformOptions.map((option) => {
                    const off = platformOff(option);
                    // A platform that isn't built yet is a different thing from
                    // one this plan can't use: it says so on the tile, and the
                    // tile still answers a press — `disabled` would swallow it,
                    // and a `title` is no help on a touch screen.
                    const soon = Boolean(option.disabled);
                    return (
                    <button
                      key={option.id}
                      type="button"
                      className={[
                        'ta-platform',
                        option.branded ? 'ta-platform--branded' : '',
                        option.id === platform.id ? 'ta-platform--on' : '',
                        off ? 'ta-platform--off' : '',
                        soon ? 'ta-platform--soon' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-pressed={option.id === platform.id}
                      disabled={off && !soon}
                      aria-disabled={off || undefined}
                      title={
                        soon
                          ? t('{{platform}} is coming soon', { platform: option.label })
                          : off
                            ? t('Free Trial accounts run on MetaTrader 5')
                            : undefined
                      }
                      onClick={() => {
                        if (soon) {
                          toast.info(
                            t('{{platform}} is coming soon', { platform: option.label }),
                            t('Our own platform is still in the works. Pick MetaTrader 5, cTrader, DX Trade or TradeLocker for now — we will announce it here when it opens.'),
                          );
                          return;
                        }
                        selectPlatform(option.id);
                      }}
                    >
                      {soon && <span className="ta-platform__soon">{t('Coming soon')}</span>}
                      <span className="ta-platform__inner">
                        <img
                          className="ta-platform__logo"
                          src={option.logo}
                          alt={option.label}
                          style={{ width: `${option.logoWidth}px` }}
                        />
                      </span>
                    </button>
                    );
                  })}
                </div>
              </section>

              {/* EA Use — directly under Platform, because picking MetaTrader is
                  what raises the question. Expert Advisors are a MetaTrader
                  feature, so no other tile asks it. */}
              {showEaUse && (
                <>
                  <span className="ta-rule" />

                  <section className="ta-section">
                    <div className="ta-section__head">
                      <h2 className="ta-section__title">{t('EA Use')}</h2>
                      <p className="ta-section__desc">
                        {t(
                          "Tell us whether you plan to trade with an Expert Advisor. It doesn't change what you pay.",
                        )}
                      </p>
                    </div>

                    <div
                      className={`ta-types${eaFill.className}`}
                      ref={eaFill.ref}
                      style={cols(eaChoices.length)}
                    >
                      {eaFill.node}
                      {eaChoices.map((option) => {
                        const off = eaChoiceOff(option);
                        return (
                          <button
                            key={option.id}
                            type="button"
                            className={[
                              'ta-type',
                              option.id === eaChoice.id ? 'ta-type--on' : '',
                              off ? 'ta-type--off' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            aria-pressed={option.id === eaChoice.id}
                            disabled={off}
                            title={off ? t(eaYesBlocked) : undefined}
                            onClick={() => chooseEa(option)}
                          >
                            <Radio on={option.id === eaChoice.id} />
                            <span className="ta-type__label">{t(option.label)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                </>
              )}

              <span className="ta-rule" />

              <div className="ta-actions">
                <button type="button" className="ta-next" onClick={() => goToStage(2)}>
                  {t('Next')}
                  <span className="ta-next__arrow" style={icon('arrow-up-right')} />
                </button>
              </div>

              <span className="ta-rule" />

              {/* Readable before step 2 asks for the consent that names it. */}
              <TermsViewer src={TERMS_PDF} />
            </div>
          ) : (
            <div className="ta-sections">
              {/* Payment Method — skipped for Free Trial, which takes no payment. */}
              {!isFreeTrial && (
                <>
                  <section className="ta-section">
                    <div className="ta-section__head">
                      <h2 className="ta-section__title">{t('Payment Method')}</h2>
                      <p className="ta-section__desc">
                        {t('Complete your purchase using the option you are most comfortable with.')}
                      </p>
                    </div>

                    <div
                      className={`ta-pays${paymentFill.className}`}
                      ref={paymentFill.ref}
                      style={cols(paymentMethods.length, 3)}
                    >
                      {paymentFill.node}
                      {paymentMethods.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`ta-pay${option.id === payment ? ' ta-pay--on' : ''}`}
                          aria-pressed={option.id === payment}
                          onClick={() => choosePayment(option.id)}
                        >
                          <Radio on={option.id === payment} />
                          <span className="ta-pay__text">
                            <span className="ta-pay__label">{option.label}</span>
                            <span className="ta-pay__note">{option.note}</span>
                          </span>
                        </button>
                      ))}
                    </div>

                    {errors.payment && <p className="ta-error">{t(errors.payment)}</p>}
                  </section>

                  <span className="ta-rule" />
                </>
              )}

              {/* Spread — same radio-row card as Account Type. */}
              <section className="ta-section">
                <div className="ta-section__head">
                  <h2 className="ta-section__title">{t('Spread')}</h2>
                  <p className="ta-section__desc">
                    {t('Pick how your execution is priced. Neither option changes what you pay today.')}
                  </p>
                </div>

                <div
                  className={`ta-types${spreadFill.className}`}
                  ref={spreadFill.ref}
                  style={cols(spreads.length)}
                >
                  {spreadFill.node}
                  {spreads.map((option) => {
                    // A swap-free account runs on the standard spread only.
                    const off = swapFreeOn && option.id === 'raw';
                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={[
                          'ta-type',
                          option.id === effectiveSpread ? 'ta-type--on' : '',
                          off ? 'ta-type--off' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        aria-pressed={option.id === effectiveSpread}
                        disabled={off}
                        title={off ? t('Not available with a swap-free account') : undefined}
                        onClick={() => setSpread(option.id)}
                      >
                        <Radio on={option.id === effectiveSpread} />
                        <span className="ta-type__label">{t(option.label)}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Payout Type — only the natively bi-weekly plans (Alpha Pro 6%
                  and 8%) have two prices to choose between; see `showPayoutType`. */}
              {showPayoutType && (
                <>
                  <span className="ta-rule" />

                  <section className="ta-section">
                    <div className="ta-section__head">
                      <h2 className="ta-section__title">{t('Payout Type')}</h2>
                      <p className="ta-section__desc">
                        {t('Choose how often you can withdraw. Unlike Spread, this one changes what you pay.')}
                      </p>
                    </div>

                    <div
                      className={`ta-types${payoutFill.className}`}
                      ref={payoutFill.ref}
                      style={cols(payoutTypes.length)}
                    >
                      {payoutFill.node}
                      {payoutTypes.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`ta-type${option.id === payout.id ? ' ta-type--on' : ''}`}
                          aria-pressed={option.id === payout.id}
                          onClick={() => setPayoutId(option.id)}
                        >
                          <Radio on={option.id === payout.id} />
                          <span className="ta-type__label">{t(option.label)}</span>
                          <span className="ta-type__price">
                            {usd(challenge.price[option.id], { decimals: 2 })}
                          </span>
                        </button>
                      ))}
                    </div>
                  </section>
                </>
              )}

              {/* Nothing to offer on Free Trial or Alpha Direct, and the 90% row
                  only exists where the plan supports the upgrade. */}
              {visibleAddOns.length > 0 && (
                <>
                  <span className="ta-rule" />

                  <section className="ta-section">
                    <div className="ta-section__head">
                      <h2 className="ta-section__title">{t('Add-ons')}</h2>
                      <p className="ta-section__desc">
                        {t('Optional upgrades applied to this account for a one-time fee.')}
                      </p>
                    </div>

                    <div className="ta-types" style={cols(visibleAddOns.length, 2)}>
                      {visibleAddOns.map((option) => {
                        const off = addOnOff(option);
                        const on = !off && addOnIds.includes(option.id);
                        const fee = addOnFee(option);
                        return (
                          <button
                            key={option.id}
                            type="button"
                            className={[
                              'ta-type',
                              on ? 'ta-type--on' : '',
                              off ? 'ta-type--off' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            aria-pressed={on}
                            disabled={off}
                            title={off ? t('Not available when trading with an Expert Advisor') : undefined}
                            onClick={() => toggleAddOn(option.id)}
                          >
                            <Radio on={on} />
                            <span className="ta-type__label">{t(option.label)}</span>
                            {fee > 0 && (
                              <span className="ta-type__price">+{usd(fee, { decimals: 2 })}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                </>
              )}

              <span className="ta-rule" />

              {/* Customer Information */}
              <section className="ta-section">
                <div className="ta-section__head">
                  <h2 className="ta-section__title">{t('Customer Information')}</h2>
                  <p className="ta-section__desc">
                    {t('Enter your personal details to complete your registration and account setup.')}
                  </p>
                </div>

                <div className="ta-fields">
                  <Input
                    label="First Name"
                    name="first_name"
                    placeholder="Enter your first name"
                    value={customer.first_name}
                    onChange={editCustomer}
                    error={errors.first_name}
                    disabled={locked('first_name')}
                  />
                  <Input
                    label="Last Name"
                    name="last_name"
                    placeholder="Enter your last name"
                    value={customer.last_name}
                    onChange={editCustomer}
                    error={errors.last_name}
                    disabled={locked('last_name')}
                  />
                  <Input
                    label="Email"
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={customer.email}
                    onChange={editCustomer}
                    error={errors.email}
                    disabled={locked('email')}
                    autoComplete="email"
                  />
                  <NumberInput
                    label="Phone Number"
                    name="contact_number"
                    placeholder="Enter your phone number"
                    value={customer.contact_number}
                    onChange={editCustomer}
                    error={errors.contact_number}
                    countryCode={`+${customer.country_code}`}
                    onCountryCodeChange={(code) =>
                      setEdits((prev) => ({ ...prev, country_code: Number(code.replace(/\D/g, '')) }))
                    }
                    disabled={locked('contact_number')}
                  />
                  <Input
                    label="City"
                    name="city"
                    placeholder="Enter your city"
                    value={customer.city}
                    onChange={editCustomer}
                    error={errors.city}
                    disabled={locked('city')}
                  />
                  <Select
                    label="Country"
                    name="country"
                    value={findCountry(customer.country)?.code}
                    onChange={({ target }) =>
                      editCustomer({ target: { name: target.name, value: countryName(target.value) } })
                    }
                    error={errors.country}
                    disabled={locked('country')}
                  />
                </div>
              </section>

              <span className="ta-rule" />

              {errors.submit && (
                <p className="ta-error ta-error--submit" role="alert">
                  {t(errors.submit)}
                </p>
              )}

              <div className="ta-actions ta-actions--split">
                <button type="button" className="ta-back" onClick={() => goToStage(1)}>
                  <span className="ta-back__arrow" style={icon('arrow-up-right')} />
                  {t('Previous')}
                </button>
                <button
                  type="button"
                  className="ta-next"
                  onClick={completePurchase}
                  // Not until the required boxes in the Order Summary are ticked (N10).
                  disabled={isPreviewing || !allConsented}
                  title={
                    allConsented ? undefined : t('Tick the boxes in the Order Summary to continue')
                  }
                >
                  {isPreviewing ? t('Reviewing…') : t('Complete Purchase')}
                  <span className="ta-next__arrow" style={icon('arrow-up-right')} />
                </button>
              </div>

              <span className="ta-rule" />

              {/* Again here, next to the consent that names it and the purchase it covers. */}
              <TermsViewer src={TERMS_PDF} />
            </div>
          )}
        </section>

        {/* Order Summary, 1:12494 */}
        <aside className="ta-summary">
          <div className="ta-summary__head">{t('Order Summary')}</div>

          <div className="ta-summary__body">
            <div className="ta-discount">
              <span className="ta-discount__label">{t('Discount Code')}</span>
              <div className="ta-discount__row">
                <input
                  ref={discountRef}
                  className="ta-discount__input"
                  type="text"
                  placeholder={t('Enter code')}
                  aria-label={t('Discount code')}
                  value={discount}
                  disabled={couponLocked}
                  // Codes are uppercase, and the endpoint matches them exactly.
                  // Folding the case on the way in rather than only on the way
                  // out means the field shows the code that will be sent.
                  onChange={(e) => editCoupon(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && !couponLocked && applyCoupon()}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {/* Locking the field with no way out would strand a trader who
                    mistyped a valid code, so Apply becomes the way back. */}
                <button
                  type="button"
                  className="ta-discount__apply"
                  onClick={couponLocked ? clearCoupon : applyCoupon}
                  disabled={pricing.isFetching}
                  aria-busy={pricing.isFetching}
                >
                  {pricing.isFetching ? <Spin size="small" /> : couponLocked ? t('Remove') : t('Apply')}
                </button>
              </div>

              {/* One line, and only once the server has ruled on the code. */}
              {couponRejected ? (
                <span className="ta-discount__note ta-discount__note--bad" role="alert">
                  {t(couponError)}
                </span>
              ) : (
                appliedCoupon &&
                !pricing.isFetching && (
                  <span className="ta-discount__note ta-discount__note--good">
                    {t('Code {{code}} applied.', { code: appliedCoupon })}
                  </span>
                )
              )}
            </div>

            <span className="ta-rule" />

            <div className="ta-summary__rows">
              {summaryRows.map((row) => (
                <div key={row.label} className="ta-summary__row">
                  <span className="ta-summary__row-label">{row.label}</span>
                  <span
                    className={`ta-summary__row-value${row.tone ? ` ta-summary__row-value--${row.tone}` : ''}`}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Checkout has no Account Type tiles to carry the rules, so they
                come here — the same switch, the same rows, the selected plan only. */}
            {stage === 2 && objectives.length > 0 && (
              <div className="ta-objectives">
                <ObjectivesSwitch
                  on={showObjectives}
                  onToggle={() => setShowObjectives((on) => !on)}
                />
                <ObjectivesList rows={objectives} open={showObjectives} />
              </div>
            )}

            <div className="ta-total">
              <span className="ta-total__text">
                <span className="ta-total__label">{t('Total due')}</span>
                <span className="ta-total__note">{t('One-time payment')}</span>
              </span>
              <span
                className={`ta-total__value${pricing.isFetching ? ' ta-total__value--pending' : ''}`}
              >
                {orderTotal == null ? '—' : usd(orderTotal, totalDigits(orderTotal))}
              </span>
            </div>

            {/* Consents — AC1-I92. Under the total, where the old checkout puts
                them, and the message sits with the boxes rather than by the
                Complete Purchase button in the other column: this is where the
                trader has to act on it. */}
            <div className="ta-consents" ref={consentsRef}>
              <span className="ta-consents__lead">{t('By confirming the order:')}</span>

              {shownConsents.map((consent) => (
                <label className="ta-consent" key={consent.id}>
                  <input
                    className="ta-consent__box"
                    type="checkbox"
                    checked={Boolean(consented[consent.id])}
                    onChange={() => toggleConsent(consent.id)}
                  />
                  <span className="ta-consent__text">
                    {t(consent.lead)}
                    {consent.links?.map((link, i) => (
                      <Fragment key={link.label}>
                        {i === 0 ? ' ' : ', '}
                        {link.href ? (
                          <a
                            className="ta-consent__link"
                            href={link.href}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t(link.label)}
                          </a>
                        ) : (
                          t(link.label)
                        )}
                      </Fragment>
                    ))}
                    {consent.links ? '.' : ''}
                    <span className="ta-consent__req">*</span>
                  </span>
                </label>
              ))}

              {errors.consent && (
                <p className="ta-error" role="alert">
                  {t(errors.consent)}
                </p>
              )}
            </div>

            {/* The live checkout's "Chat with us" (N79): the Help centre, where
                every way to reach support (Discord, email, the FAQ) is listed. */}
            <div className="ta-help">
              <span className="ta-help__text">
                <span className="ta-help__title">{t('Need some help?')}</span>
                <span className="ta-help__note">{t('We are here for you. Write us a message.')}</span>
              </span>
              <Link className="ta-help__chat" to="/help-centre">
                {t('Chat with us')}
              </Link>
            </div>
          </div>
        </aside>
      </div>

      <PurchaseReviewDialog
        open={Boolean(preview)}
        preview={preview}
        error={submitError}
        isSubmitting={isRedirecting || isCreatingTrial}
        onCancel={closeReview}
        onConfirm={confirmPurchase}
      />

      <CryptoWarningDialog
        open={cryptoWarningOpen}
        onCancel={() => setCryptoWarningOpen(false)}
        onProceed={() => {
          setPayment('coinspayment');
          setCryptoWarningOpen(false);
        }}
      />
    </div>
  );
}
